import { Check, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  THEMES,
  THEME_NAMES,
  useTheme,
  type PrimaryStyle,
  type ThemeName,
} from './ThemeProvider';

/**
 * Topbar palette picker. Opens a popover with five swatches (one
 * per editorial palette) and a segmented toggle for the
 * ink-vs-accent primary-button style. Mirrors the mobile
 * settings/theme.tsx screen but condensed for a topbar dropdown —
 * the full picker also lives in settings/theme for users who want
 * the side-by-side preview tiles.
 */
export function ThemeToggle() {
  const { theme, primaryStyle, setTheme, setPrimaryStyle, meta } = useTheme();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          title={`Theme — ${meta.label}`}
          aria-label={`Theme (currently ${meta.label})`}
        >
          <Palette className="size-[18px]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[300px] p-0">
        <div className="px-4 pt-4 pb-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Palette
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Switching is instant. Pick what feels right for your brand.
          </p>
        </div>

        <div className="flex flex-col gap-1 px-2 pb-2">
          {THEME_NAMES.map((name) => (
            <ThemeRow
              key={name}
              name={name}
              active={theme === name}
              onSelect={() => setTheme(name)}
            />
          ))}
        </div>

        <div className="border-t border-line px-4 pt-3 pb-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Primary buttons
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Send / Save / Mark paid colour across the app.
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <PrimaryStyleTile
              label="Ink"
              fill={THEMES[theme].preview.ink}
              fg={THEMES[theme].preview.surface}
              active={primaryStyle === 'ink'}
              onSelect={() => setPrimaryStyle('ink')}
            />
            <PrimaryStyleTile
              label="Accent"
              fill={THEMES[theme].preview.accent}
              fg="#FFFFFF"
              active={primaryStyle === 'accent'}
              onSelect={() => setPrimaryStyle('accent')}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ThemeRow({
  name,
  active,
  onSelect,
}: {
  name: ThemeName;
  active: boolean;
  onSelect: () => void;
}) {
  const meta = THEMES[name];
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={cn(
        'flex items-center gap-3 rounded-md px-2 py-2 text-left transition',
        active ? 'bg-surface-alt' : 'hover:bg-surface-alt/60',
      )}
    >
      <span
        className="relative inline-block size-9 shrink-0 overflow-hidden rounded-md ring-1 ring-line"
        style={{ background: meta.preview.bg }}
        aria-hidden
      >
        <span
          className="absolute inset-y-0 left-0 w-1/2"
          style={{ background: meta.preview.surface }}
        />
        <span
          className="absolute right-1.5 bottom-1.5 size-3 rounded-sm"
          style={{ background: meta.preview.accent }}
        />
        <span
          className="absolute top-1.5 left-1.5 h-1.5 w-3.5 rounded-full"
          style={{ background: meta.preview.ink }}
        />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-foreground">{meta.label}</span>
        <span className="block truncate text-[11px] text-muted-foreground">
          {meta.description}
        </span>
      </span>
      {active ? (
        <Check className="size-4 shrink-0 text-foreground" strokeWidth={2.5} />
      ) : null}
    </button>
  );
}

function PrimaryStyleTile({
  label,
  fill,
  fg,
  active,
  onSelect,
}: {
  label: PrimaryStyle | string;
  fill: string;
  fg: string;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-md border px-2 py-2 transition',
        active
          ? 'border-foreground/80 bg-surface-alt'
          : 'border-line hover:border-line-strong',
      )}
    >
      <span
        className="rounded-md px-3 py-1 text-[11px] font-semibold"
        style={{ background: fill, color: fg }}
      >
        Send
      </span>
      <span className="text-[11px] font-medium text-foreground">{label}</span>
    </button>
  );
}
